import { ErrorBoundary } from '@src/components/ErrorBoundary';
import { NavigationSidebar } from '@src/components/NavigationSidebar';
import { Header } from '@src/containers/Header';
import { NoMatchPage } from '@src/containers/NoMatchPage';
import { useFeatureFlag, useRoleAuth } from '@src/hooks';
import { getDisplayMode } from '@src/utils/getDisplayMode';
import {
  ACTION,
  AppwideDrawer,
  Checkbox,
  Dialog,
  Flex,
  Grid,
  isSandboxEnv,
  LoaderTalos,
  MixpanelEvent,
  MixpanelEventProperty,
  Notes,
  parseDimension,
  useAppwideDrawerContext,
  useEventListener,
  useGlobalDialog,
  useMixpanel,
} from '@talos/kyoko';
import lazyWithPrefetch from '@talos/kyoko/src/utils/lazyWithPrefetch';
import { noop } from 'lodash-es';
import { memo, startTransition, Suspense, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { useAdminHeaderContext } from '../../providers/AdminHeaderContext';
import { useAppStateSelector } from '../../providers/AppStateProvider';
import { useDisplaySettings } from '../../providers/DisplaySettingsProvider';
import { NotificationDeepLinkRedirect } from './NotificationDeepLinkRedirect';
import { TransfersRouteRedirect } from './TransfersRouteRedirect';

export const Content = styled(Grid)<{ hasDrawer?: boolean; hasOMS?: boolean }>`
  flex: 1 1 0;
  overflow: hidden;
  background: ${({ theme }) => theme.backgroundBody};
  grid-template-areas:
    'sidebar header header'
    'sidebar main drawer';
  grid-template-columns: ${({ hasDrawer, hasOMS }) => {
    const columns = ['auto', '1fr'];
    if (hasDrawer || hasOMS) {
      columns.push('min-content');
    }
    return columns.join(' ');
  }};
  grid-template-rows: auto 1fr;
  width: 100%;
  height: 100%;
`;

const MainArea = styled(Flex)<{ gap: string }>`
  grid-area: main;
  overflow: hidden;
  gap: ${({ gap, theme }) => parseDimension(theme, gap)};
`;

const DrawerArea = styled(Flex)`
  grid-area: drawer;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
`;

const TalosAdminContainer = lazyWithPrefetch(() => import('@src/containers/TalosAdmin'));
const AdminContainer = lazyWithPrefetch(() => import('@src/containers/Admin'));
const PortfolioContainer = lazyWithPrefetch(() => import('@src/containers/Portfolio'));
const PortfolioEngineeringContainer = lazyWithPrefetch(() => import('@src/containers/PortfolioEngineering'));
const DealerContainer = lazyWithPrefetch(() => import('@src/containers/Dealer'));
const AnalyticsContainer = lazyWithPrefetch(() => import('@src/containers/Analytics'));
const MonitoringContainer = lazyWithPrefetch(() => import('@src/containers/Monitoring'));
const SettingsContainer = lazyWithPrefetch(() => import('@src/containers/Settings'));
const OMS = lazyWithPrefetch(() => import('@src/components/OMS'));
const PowerSearch = lazyWithPrefetch(() => import('../../components/PowerSearch/PowerSearch'));
const CredentialsAgeBanner = lazyWithPrefetch(
  () => import('../../components/CredentialsAgeBanner/CredentialsAgeBanner')
);
const TradingRoute = lazyWithPrefetch(() => import('@src/containers/Trading/TradingRoute'));
const MonitoringRoute = lazyWithPrefetch(() => import('@src/containers/Monitoring/MonitoringRoute'));

// Workaround for UI-5968.
// When a new version is deployed, these files will no longer exist, and so
// we prefetch them to avoid the page crashing.
AdminContainer.prefetch();
TalosAdminContainer.prefetch();
PortfolioContainer.prefetch();
PortfolioEngineeringContainer.prefetch();
DealerContainer.prefetch();
AnalyticsContainer.prefetch();
MonitoringContainer.prefetch();
MonitoringRoute.prefetch();
SettingsContainer.prefetch();
OMS.prefetch();
PowerSearch.prefetch();
CredentialsAgeBanner.prefetch();
TradingRoute.prefetch();

const TradeMatchRedirect = () => {
  const location = useLocation();
  const newPath = location.pathname.replace('trade', 'trading');
  return <Navigate to={newPath} replace />;
};

export const Pages = memo(function Pages() {
  const { isAuthorized } = useRoleAuth();
  const location = useLocation();
  const mixpanel = useMixpanel();

  const path = location?.pathname;
  const section = path.split('/')[1] ?? 'trading';

  useEffect(() => {
    mixpanel.time_event(MixpanelEvent.ViewSection);
    return () => {
      mixpanel.track(MixpanelEvent.ViewSection, { [MixpanelEventProperty.Section]: section });
    };
  }, [section, mixpanel]);

  useEventListener('beforeunload', () => {
    mixpanel.track(MixpanelEvent.ViewSection, { [MixpanelEventProperty.Section]: section });
  });

  const { enableMonitoringBlotters, enableMonitoringSpaces } = useFeatureFlag();
  const { content, ...dialog } = useGlobalDialog();
  const { termsAccepted } = useDisplaySettings();

  const displayMode = getDisplayMode() ?? 'browser';
  const isInstalledApp = ['standalone', 'minimal-ui'].includes(displayMode);

  const useFlexibleUI = ['trading'].includes(section);
  const { showAdminHeader } = useAdminHeaderContext();

  const { backgroundBody, spacingLayout, spacing2 } = useTheme();

  const layoutSpacing = useFlexibleUI ? 'spacingLayout' : 'spacing2';

  // Determine if drawer and OMS should be shown based on context
  const hasDrawer = useAppwideDrawerContext().isOpen; // You can add logic here to conditionally show drawer
  const hasOMS = useAppStateSelector(state => state.OMS.openedView !== undefined); // You can add logic here to conditionally show OMS

  if (!termsAccepted && isSandboxEnv) {
    return (
      <ErrorBoundary>
        <TermsOfService />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <Dialog {...dialog}>{content}</Dialog>
      <PowerSearch />
      <Notes />
      <Suspense>
        <CredentialsAgeBanner />
      </Suspense>

      <Content
        hasDrawer={hasDrawer}
        hasOMS={hasOMS}
        gap={layoutSpacing}
        borderTop={
          !showAdminHeader && isInstalledApp
            ? `${useFlexibleUI ? spacingLayout : spacing2}px solid ${backgroundBody}`
            : 'none'
        }
        transition="border-top-width 2s"
      >
        <NavigationSidebar gridArea="sidebar" />
        <Header />

        <MainArea gap={layoutSpacing}>
          <Suspense fallback={<LoaderTalos />}>
            <Routes>
              <Route path="/" element={<Navigate to="/trading" replace />} />
              <Route path="/trade" element={<Navigate to="/trading" replace />} />
              <Route path="/trade/*" element={<TradeMatchRedirect />} />
              <Route path="/dealer" element={<Navigate to="/dealer/monitoring" replace />} />
              <Route path="/notifications/:notificationId" element={<NotificationDeepLinkRedirect />} />
              <Route path="/trading/*" element={<TradingRoute />} />
              {isAuthorized(ACTION.SUBMIT_TRANSFER) && <Route path="/transfers" element={<TransfersRouteRedirect />} />}
              {isAuthorized(ACTION.VIEW_PORTFOLIO) && <Route path="/portfolio/*" element={<PortfolioContainer />} />}
              {isAuthorized(ACTION.EDIT_PORTFOLIO_ENGINEERING) && (
                <Route path="/portfolio-engineering/*" element={<PortfolioEngineeringContainer />} />
              )}
              {isAuthorized(ACTION.VIEW_DEALER) && <Route path="/dealer/*" element={<DealerContainer />} />}
              {isAuthorized(ACTION.VIEW_ANALYTICS) && <Route path="/analytics/*" element={<AnalyticsContainer />} />}
              <Route path="/settings/*" element={<SettingsContainer />} />
              {!enableMonitoringSpaces && (
                <Route path="/monitoring" element={<Navigate to="/monitoring/blotters" replace />} />
              )}
              {enableMonitoringBlotters && !enableMonitoringSpaces && (
                <Route path="/monitoring/*" element={<MonitoringContainer />} />
              )}
              {enableMonitoringBlotters && enableMonitoringSpaces && (
                <Route path="/monitoring/*" element={<MonitoringRoute />} />
              )}
              <Route path="/admin/*" element={<AdminContainer />} />
              {isAuthorized(ACTION.VIEW_TALOS_ADMIN_SETTINGS) && (
                <Route path="/talos-admin/*" element={<TalosAdminContainer />} />
              )}
              <Route path="*" element={<NoMatchPage />} />
            </Routes>
          </Suspense>
        </MainArea>
        <DrawerArea gap={layoutSpacing}>
          <AppwideDrawer />
          <OMS />
        </DrawerArea>
      </Content>
    </ErrorBoundary>
  );
});

function TermsOfService() {
  const { setTermsAccepted } = useDisplaySettings();
  const [checked, setChecked] = useState(false);

  const onConfirm = () => startTransition(() => setTermsAccepted(true));

  return (
    <div>
      <Dialog
        isOpen={true}
        close={noop}
        showClose={false}
        showCancel={false}
        onConfirm={onConfirm}
        confirmDisabled={!checked}
        confirmLabel="Accept and Continue"
        title="Terms of Service"
      >
        <Checkbox onChange={e => setChecked(e.target.checked)} checked={checked}>
          I have read and agree to the{' '}
          <a href={TOS_URL} rel="noreferrer" target="_blank">
            Terms of Service
          </a>{' '}
          on behalf of my organization.
        </Checkbox>
      </Dialog>
    </div>
  );
}

const TOS_URL =
  'https://vault.pactsafe.io/s/976f1d9b-c61f-468e-b31e-65b7e5086bae/core-trading-platform-trial-evaluation-terms.html';
